import React, { FC, Fragment, useContext } from 'react';
import { FeedEditorCtx } from '@go-client/context/FeedEdtitorContext';
import { FeedArea } from '@go-shared/types/feedTemplate';
import { filterRundownEventsDailyProgram, groupRundownEvents } from '@go-shared/lib/feed';
import { timeToMinutes } from '@go-shared/lib/time';
import { cssFriendly, safeIdCompare } from '@go-shared/lib/utils';
import { DailyProgram, DailyProgramEvent } from '@go-shared/types/dailyProgram';
import { ContentTemplateParams } from '@go-shared/types/contentTemplate';
import { BrandAttribute, BrandEventHighlight } from '@go-shared/types/brand';
import { PropertyZone } from '@go-shared/types/property';

function makeDailyProgramRundownEventSorter(dayStart: number) {
  return (e1: DailyProgramEvent, e2: DailyProgramEvent) => {
    let startTime1 = timeToMinutes(e1.startTime, dayStart);
    let startTime2 = timeToMinutes(e2.startTime, dayStart);

    let duration1 = e1.duration;
    let duration2 = e2.duration;

    // sort by start time
    if (startTime1 !== startTime2) return startTime1 - startTime2;

    // if they are the same, sort by end time
    if (duration1 !== duration2) return duration1 - duration2;

    // otherwise, sort by title
    return e1.title.localeCompare(e2.title);
  };
}

interface FilteredRundownProps {
  area: FeedArea;
  dailyProgram?: DailyProgram;
}

// FIXME: This is copied from server/lib/feed.ts
function getFilteredRundownDeckPrefix(defaultDeckValue: string, params?: ContentTemplateParams) {
  // If params and eventRundownDeckPrefix are defined...
  if (params && params.eventRundownDeckPrefix !== undefined) {
    // if the prefix is the empty string, return undefined
    if (params.eventRundownDeckPrefix === '') return undefined;
    // Otherwise return the value
    return params.eventRundownDeckPrefix;
  }
  // If params or eventRundownDeckPrefix are undefined, return the default ('Deck')
  return defaultDeckValue;
}

function fontSizeToLineHeight(fontSize: number) {
  let increment = (fontSize - 16) * 0.125;

  return 1.5 + increment;
}

type RundownEventProps = {
  event: DailyProgramEvent;
  deckPreffix?: string;
  attributes: BrandAttribute[];
  highlights: BrandEventHighlight[];
  zones: PropertyZone[];
  area: FeedArea;
};
const RundownEvent: FC<RundownEventProps> = ({
  event,
  deckPreffix,
  attributes,
  highlights,
  zones,
}) => {
  function getEventLocation(e: DailyProgramEvent, deckPrefix?: string): React.JSX.Element {
    return (
      <>
        <span>{e.venueDisplay.name}</span>
        <br />
        {e.deckDisplayName ? (
          <span>{e.deckDisplayName}</span>
        ) : (
          deckPrefix && (
            <>
              <span>
                {deckPrefix} {' ' + e.venueDisplay.level}
              </span>
              <span> {e.venueDisplay.venue_location}</span>
            </>
          )
        )}
      </>
    );
  }

  return (
    <div
      className="feed-rundown-group-row flex border-b border-grey-dark py-1"
      style={{
        breakInside: 'avoid',
        width: '100%',
      }}
    >
      <div className="px-1" style={{ width: '20%' }}>
        {event.timeString}
      </div>

      <div className="px-1" style={{ width: '50%' }}>
        {event.title}
        <span className="hidden rundown-description-inline">{event.description}</span>
      </div>

      <div className="px-1" style={{ width: '30%' }}>
        {getEventLocation(event, deckPreffix)}
      </div>

      <div className="hidden rundown-attributes">
        {attributes.map(a => {
          let imgDiv = <></>;
          let nameDiv = (
            <span className={`rundown-attribute_${cssFriendly(a.name)}`}>
              <span>{a.name}</span>
            </span>
          );
          if (a.iconUrl) {
            imgDiv = <img src={a.iconUrl} />;
          }
          return (
            <Fragment key={a._id}>
              {imgDiv}
              {nameDiv}
            </Fragment>
          );
        })}

        {highlights.map(a => {
          let nameDiv = (
            <span className={`rundown-highlight_${cssFriendly(a.name)}`}>
              <span>{a.name}</span>
            </span>
          );

          return <Fragment key={a._id}>{nameDiv}</Fragment>;
        })}
      </div>

      <div className="hidden rundown-spacing">
        <span></span>
      </div>

      <div className="hidden rundown-zones">
        {zones.map(z => {
          let nameDiv = (
            <span className={`rundown-zone_${cssFriendly(z.name)}`}>
              <span>{z.name}</span>
            </span>
          );

          return <Fragment key={z._id}>{nameDiv}</Fragment>;
        })}
      </div>
      <div className={`hidden rundown-trending${event.isTrending ? ' is-trending' : ''}`}>
        <span></span>
      </div>

      <div className={`hidden rundown-priced${event.isPriced ? ' is-priced' : ''}`}>
        <span></span>
      </div>
    </div>
  );
};

const RundownHeader: FC = () => {
  return (
    <>
      <div className="px-1" style={{ width: '20%' }}>
        TIME
      </div>
      <div className="px-1" style={{ width: '50%' }}>
        ACTIVITY
      </div>
      <div className="px-1" style={{ width: '30%' }}>
        LOCATION
      </div>
    </>
  );
};

export const FilteredRundown: FC<FilteredRundownProps> = ({
  area,
  dailyProgram: dailyProgramProp,
}) => {
  let { property, rundownFontSize, dailyProgram: dailyProgramContext } = useContext(FeedEditorCtx);
  let dailyProgram = dailyProgramProp || dailyProgramContext;

  let eventSorter = makeDailyProgramRundownEventSorter(360);

  if (dailyProgram === undefined) {
    return <div>Loading ...</div>;
  }

  let deckPrefix = getFilteredRundownDeckPrefix(
    dailyProgram?.collections.commonTexts.deck,
    area.params
  );

  const events = dailyProgram.events;

  let filteredRundownEvents =
    area.params && area.params.eventRundownFilter
      ? filterRundownEventsDailyProgram(dailyProgram, area.params.eventRundownFilter)
      : events;

  filteredRundownEvents.sort(eventSorter);

  let grouping = area.params ? area.params.eventRundownTimeGrouping : undefined;
  let groupedRundownEvents = groupRundownEvents(filteredRundownEvents, grouping);
  let lineHeight = fontSizeToLineHeight(rundownFontSize);

  return (
    <div
      style={{ lineHeight, fontSize: 10 }}
      className="feed-rundown relative w-full"
      key={area._id}
    >
      <div
        className="relative w-full flex flex-row flex-wrap"
        style={{
          top: 0,
          left: 0,
          maxHeight: '100%',
        }}
      >
        {area.params && area.params.eventRundownTitle && (
          <h1 className="feed-rundown-title">{area.params.eventRundownTitle}</h1>
        )}
        <div className="feed-rundown-body relative w-full flex flex-row flex-wrap">
          <div
            className="feed-rundown-header flex border-b border-grey-dark py-1 font-bold"
            style={{
              breakInside: 'avoid',
              width: '100%',
            }}
          >
            <RundownHeader />
          </div>

          {groupedRundownEvents.map((eventGroup, i) => (
            <React.Fragment key={`event-group-${i}`}>
              {eventGroup.groupName && (
                <h1 className="feed-rundown-group-title">{eventGroup.groupName}</h1>
              )}
              <div
                className="feed-rundown-group-header flex border-b border-grey-dark py-1 font-bold"
                style={{
                  breakInside: 'avoid',
                  width: '100%',
                }}
              >
                <RundownHeader />
              </div>

              {eventGroup.events.map(e => {
                const eventHighlights =
                  property?.brand.eventHighlights.filter(
                    a => (e.highlightIds || []).findIndex(b => safeIdCompare(a._id, b)) > -1
                  ) || [];

                const attributes =
                  property?.brand.attributes.filter(
                    a => (e.attributeIds || []).findIndex(b => safeIdCompare(a._id, b)) > -1
                  ) || [];

                const zones =
                  property?.zones.filter(
                    z => (e.zoneIds || []).findIndex(b => safeIdCompare(z._id, b)) > -1
                  ) || [];

                return (
                  <RundownEvent
                    event={e}
                    deckPreffix={deckPrefix}
                    attributes={attributes}
                    highlights={eventHighlights}
                    area={area}
                    zones={zones}
                  />
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};
